// src/pages/CallFailures.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Search, RefreshCw, X, AlertCircle, PhoneOff, CheckCircle } from 'lucide-react';
import { callHangupMetricsService, HangupMetric, HangupSummaryRow } from 'api/services/callHangupMetrics.service';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const INPUT = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm';
const isAnswered = (d?: string) => (d || '').toUpperCase() === 'ANSWER' || (d || '').toUpperCase() === 'ANSWERED';

const dispBadge = (d?: string) => {
  const up = (d || '').toUpperCase();
  if (isAnswered(d)) return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">{d}</span>;
  const amber = ['NO ANSWER', 'NO_ANSWER', 'BUSY'];
  const cls = amber.includes(up) ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`px-2 py-0.5 text-xs rounded-full ${cls}`}>{d || '—'}</span>;
};
const fmt = (s?: string) => (s ? new Date(s.replace(' ', 'T')).toLocaleString() : '—');

const CallFailures: React.FC = () => {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;

  const [summary, setSummary] = useState<HangupSummaryRow[]>([]);
  const [rows, setRows] = useState<HangupMetric[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState({ disposition: '', callerId: '', dialedNumber: '' });
  const [detail, setDetail] = useState<HangupMetric | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [sumRes, listRes] = await Promise.all([
      callHangupMetricsService.summary(orgId),
      callHangupMetricsService.list({ organizationId: orgId, perPage: 50, ...filter, disposition: filter.disposition || undefined, callerId: filter.callerId || undefined, dialedNumber: filter.dialedNumber || undefined }),
    ]);
    if (sumRes.success) setSummary(sumRes.data || []);
    if (listRes.success && listRes.data) { setRows(listRes.data.data || []); setTotal(listRes.data.pagination?.total || 0); }
    else if (!listRes.success) setError(listRes.error || 'Failed to load');
    setLoading(false);
  }, [orgId, filter]);
  useEffect(() => { load(); }, [load]);

  const totals = summary.reduce((acc, r) => {
    acc.total += r.count;
    if (isAnswered(r.disposition)) acc.answered += r.count; else acc.failed += r.count;
    return acc;
  }, { total: 0, answered: 0, failed: 0 });
  const topFailures = summary.filter(r => !isAnswered(r.disposition)).slice(0, 6);

  const openDetail = async (uid: string) => {
    const res = await callHangupMetricsService.detail(uid, orgId);
    if (res.success && res.data) setDetail(res.data);
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><ShieldAlert className="w-6 h-6 text-red-600" /> Call Failures</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Why calls end — disposition &amp; hangup cause per call, from the runtime CDR.</p>
        </div>
        <button onClick={load} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500">Total calls</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{totals.total}</p></div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />Answered</p><p className="text-2xl font-bold text-green-600">{totals.answered}</p></div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500 flex items-center gap-1"><PhoneOff className="w-3 h-3 text-red-500" />Not answered / failed</p><p className="text-2xl font-bold text-red-600">{totals.failed}</p></div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500">Answer rate</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{totals.total ? Math.round((totals.answered / totals.total) * 100) : 0}%</p></div>
      </div>

      {/* Top failure reasons */}
      {topFailures.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Top failure reasons</p>
          <div className="flex flex-wrap gap-2">
            {topFailures.map((r, i) => (
              <button key={i} onClick={() => setFilter({ ...filter, disposition: r.disposition || '' })}
                className="px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-sm text-red-700 flex items-center gap-2">
                <span className="font-medium">{r.disposition || '—'}</span>
                <span className="text-xs text-red-500">cause {r.hangup_cause ?? '—'} · {r.hangup_cause_txt || ''}</span>
                <span className="font-bold">{r.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-gray-500 mb-1">Disposition</label>
          <input value={filter.disposition} onChange={e => setFilter({ ...filter, disposition: e.target.value })} className={`${INPUT} w-40`} placeholder="e.g. FAILED" /></div>
        <div><label className="block text-xs text-gray-500 mb-1">Caller</label>
          <input value={filter.callerId} onChange={e => setFilter({ ...filter, callerId: e.target.value })} className={`${INPUT} w-40`} placeholder="caller id" /></div>
        <div><label className="block text-xs text-gray-500 mb-1">Dialed</label>
          <input value={filter.dialedNumber} onChange={e => setFilter({ ...filter, dialedNumber: e.target.value })} className={`${INPUT} w-40`} placeholder="dialed number" /></div>
        <button onClick={load} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 inline-flex items-center gap-2"><Search className="w-4 h-4" /> Search</button>
        {filter.disposition && <button onClick={() => setFilter({ ...filter, disposition: '' })} className="text-sm text-gray-500 hover:text-gray-700">clear</button>}
      </div>

      {/* List */}
      {loading ? <LoadingSpinner /> : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>{['When', 'Caller', 'Dialed', 'Disposition', 'Hangup cause', 'Duration', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">No hangup metrics for this filter.</td></tr>}
              {rows.map(r => (
                <tr key={r.unique_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{fmt(r.created_at || r.call_ended_at)}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">{r.caller_id || '—'}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">{r.dialed_number || '—'}</td>
                  <td className="px-4 py-3">{dispBadge(r.disposition)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{r.hangup_cause != null ? <span className="font-mono">{r.hangup_cause}</span> : ''} {r.hangup_cause_txt || ''}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{r.duration_seconds != null ? `${r.duration_seconds}s` : '—'}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => openDetail(r.unique_id)} className="text-xs text-blue-600 hover:underline">details</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-700">{rows.length} of {total} shown</div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Call {detail.unique_id}</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                {[['Caller', detail.caller_id], ['Dialed', detail.dialed_number], ['Disposition', detail.disposition], ['Hangup cause', `${detail.hangup_cause ?? ''} ${detail.hangup_cause_txt || ''}`], ['Channel', detail.channel], ['Channel state', detail.channel_state], ['Last app', detail.last_app], ['Duration', detail.duration_seconds != null ? `${detail.duration_seconds}s` : '—'], ['Started', fmt(detail.call_started_at)], ['Ended', fmt(detail.call_ended_at)], ['Business', detail.business_name || detail.business_id], ['Service', detail.service_id]].map(([k, v]) => (
                  <div key={k as string}><span className="text-xs text-gray-500">{k}</span><p className="text-gray-800 dark:text-gray-200 font-mono break-all">{(v as any) || '—'}</p></div>
                ))}
              </div>
              {(['raw_cdr', 'raw_hangup', 'hangup_meta'] as const).map(k => detail[k] && (
                <div key={k}>
                  <p className="text-xs font-semibold text-gray-500 mt-2">{k}</p>
                  <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded p-2 overflow-x-auto max-h-48 text-gray-700 dark:text-gray-300">{typeof detail[k] === 'string' ? detail[k] : JSON.stringify(detail[k], null, 2)}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallFailures;
